import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, Typography } from '@mui/material';
import theme from './theme';
import Sidebar from './components/Sidebar';
import AnomaliesTab from './components/AnomaliesTab';
import RulesTab from './components/RulesTab';
import RuleSynthesisTab from './components/RuleSynthesisTab/RuleSynthesisTab';
import JobTrackerTab from './components/JobTrackerTab';
import SkillsListPage from './components/SkillsListPage';
import SkillOnboardingForm from './components/SkillOnboardingForm';
import SkillCsvOnboardingPage from './components/SkillCsvOnboardingPage';
import SkillEditorPage from './components/SkillEditorPage';
import MetricsTab from './components/MetricsTab';
import AdminConsole from './components/AdminConsole';
import SettingsPage from './components/SettingsPage';
import LoginPage, { POST_LOGIN_REDIRECT_KEY } from './components/LoginPage';
import UserMenu from './components/UserMenu';
import Loading from './components/Loading';
import { useAuth } from './contexts/AuthContext';

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, isAuthenticated, isAdmin } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Restore a deep link that was stashed (see LoginPage.js) before the
  // full-page OAuth round trip. Runs once per sign-in; `replace` so it
  // doesn't leave the transient "/" the OAuth callback lands on in history.
  useEffect(() => {
    if (!isAuthenticated) return;
    let saved = null;
    try { saved = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY); } catch { /* ignore */ }
    if (!saved) return;
    try { sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY); } catch { /* ignore */ }
    if (saved.startsWith('/') && !saved.startsWith('//')) {
      navigate(saved, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Determine active tab from pathname
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.startsWith('/anomalies')) return 'anomalies';
    if (path.startsWith('/metrics')) return 'metrics';
    if (path.startsWith('/rules')) return 'rules';
    if (path.startsWith('/rule-synthesis')) return 'rule-synthesis';
    if (path.startsWith('/jobs')) return 'jobs';
    if (path.startsWith('/skills')) return 'skills';
    if (path.startsWith('/settings')) return 'settings';
    if (path.startsWith('/admin')) return 'admin';
    return 'anomalies';
  };
  
  const activeTab = getActiveTab();

  if (loading) {
    return <Loading message="Loading..." />;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const getPageTitle = () => {
    switch(activeTab) {
      case 'anomalies': return 'Anomalies';
      case 'metrics': return 'Metrics';
      case 'rules': return 'Rule Management';
      case 'rule-synthesis': return 'Rule Synthesis Review';
      case 'jobs': return 'Healing Job Tracker';
      case 'skills': return 'Skills';
      case 'settings': return 'Settings';
      case 'admin': return 'Admin Console';
      default: return 'Anomalies';
    }
  };

  const getPageSubtitle = () => {
    switch(activeTab) {
      case 'anomalies': return 'Review and classify deduplicated anomalies across all reports';
      case 'metrics': return 'Anomaly and healing metrics for a selected date range';
      case 'rules': return 'Manage and configure validation rules';
      case 'rule-synthesis': return 'Review nightly rule-synthesis batches by day';
      case 'jobs': return 'Track healing jobs triggered from anomaly reports';
      case 'skills': return 'Onboard and manage retailer attribute extraction skills';
      case 'settings': return 'Manage your account and integration settings';
      case 'admin': return 'Manage users and access control';
      default: return '';
    }
  };

  return (
    <Box 
      sx={{
        display: 'flex',
        minHeight: '100vh',
        background: '#0f172a',
      }}
    >
      <Sidebar 
        activeTab={activeTab} 
        isAdmin={isAdmin}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          overflowX: 'hidden',
          marginLeft: sidebarCollapsed ? '70px' : '240px',
          minHeight: '100vh',
          background: '#0f172a',
          transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            background: '#1e293b',
            color: 'white',
            py: 3,
            px: 4,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                mb: 1,
                fontSize: '1.75rem'
              }}
            >
              {getPageTitle()}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1rem'
              }}
            >
              {getPageSubtitle()}
            </Typography>
          </Box>
          <UserMenu user={user} />
        </Box>

        {/* Main Content with Routes */}
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Routes>
            <Route path="/" element={<Navigate to="/anomalies" replace />} />
            <Route path="/anomalies" element={<AnomaliesTab />} />
            <Route path="/metrics" element={<MetricsTab />} />
            <Route path="/reports" element={<Navigate to="/anomalies" replace />} />
            <Route path="/reports-legacy" element={<Navigate to="/anomalies" replace />} />
            <Route path="/rules" element={<RulesTab />} />
            <Route path="/rule-synthesis" element={<RuleSynthesisTab />} />
            <Route path="/jobs" element={<JobTrackerTab />} />
            <Route path="/skills" element={<SkillsListPage />} />
            <Route path="/skills/onboard" element={<SkillOnboardingForm />} />
            <Route path="/skills/onboard/csv" element={<SkillCsvOnboardingPage />} />
            <Route path="/skills/:retailer/:attribute" element={<SkillEditorPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            {isAdmin && <Route path="/admin" element={<AdminConsole />} />}
            <Route path="*" element={<Navigate to="/anomalies" replace />} />
          </Routes>
        </Container>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;

